import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/ChangelogPage.css';

const ChangelogPage = () => {
  const [selectedYear, setSelectedYear] = useState('2026');
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [yearData, setYearData] = useState([]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Datos específicos del changelog para 2025
  const changelogData = useMemo(() => ({
    '2026': {
      '3': [
        {
          id: 102,
          date: '2026-03-11',
          type: 'improvement',
          title: 'Documentación actualizada de campos personalizados',
          description: 'La guía de Campos de Usuario Personalizados (CUFs) fue renovada por completo con el listado actualizado de todos los campos del sistema, organizados por categoría y con descripciones en español.',
          changes: [
            'Nuevas secciones: Anuncios, Facebook Lead Ads, Pipelines, Viber, Voz, Cuestionarios y Comentarios automáticos de Facebook/Instagram',
            'Campos agregados: {{inbox_link}}, {{me}}, {{webchat}}, {{last_ctwa}}, {{last_commented_post_text}}, {{viber_bot_username}}, {{last_call_recorded}}, {{last_opportunity_id}}, {{last_points}} y más',
            'E-commerce ampliado: carrito, pedidos y productos con todos los campos disponibles y sus triggers asociados',
            'Consulta la guía completa en Documentación → Campos de Usuario Personalizados (CUFs)'
          ]
        }
      ],
      '2': [
        {
          id: 100,
          date: '2026-02-14',
          type: 'feature',
          title: 'Retraso inteligente de respuesta',
          description: 'La IA espera a que el usuario termine antes de responder: cuando llegan varios mensajes seguidos, se combinan en una única respuesta clara en lugar de generar respuestas separadas por cada mensaje. El temporizador de retraso se reinicia con cada nuevo mensaje entrante.',
          changes: [
            'Tiempo de espera personalizable: ajusta el retraso desde Configuración para adaptarlo a tu flujo de conversación preferido',
            'Hilos más limpios e interacciones más naturales para tus usuarios'
          ]
        },
        {
          id: 101,
          date: '2026-02-08',
          type: 'feature',
          title: 'Acciones de automatización en pipeline',
          description: 'Gestiona oportunidades directamente desde tus automatizaciones: establece o borra valores de campos personalizados en cualquier oportunidad, y transfiere oportunidades entre pipelines, todo sin intervención manual.',
          changes: [
            'Tres nuevas acciones disponibles: Establecer campo personalizado, Borrar campo personalizado y Transferir a pipeline',
            'Construye flujos de trabajo más inteligentes alrededor de tus procesos de ventas y soporte'
          ]
        }
      ],
      '1': [
        {
          id: 97,
          date: '2026-01-29',
          type: 'feature',
          title: 'Contexto de URL para Agentes de IA',
          description: 'Los agentes de IA ahora pueden leer contenido de enlaces compartidos: los agentes Gemini y Claude pueden recuperar y comprender información directamente desde cualquier URL. Comparte un enlace → La IA lee la página → Responde con información precisa y contextual.',
          changes: [
            'Contexto de URL vs Búsqueda Web: La Búsqueda Web escanea internet para obtener respuestas. El Contexto de URL se enfoca únicamente en el enlace exacto proporcionado, garantizando respuestas precisas y relevantes.',
            'Perfecto para páginas de productos, documentación o cualquier contenido específico que desees que tu IA referencie'
          ]
        },
        {
          id: 98,
          date: '2026-01-28',
          type: 'feature',
          title: 'Control de dominios en búsqueda web de IA',
          description: 'Restringe la búsqueda de IA a sitios web aprobados: Elige exactamente de qué dominios puede extraer información tu IA. Mantén los resultados enfocados en fuentes confiables y evita datos de sitios de la competencia.',
          changes: [
            'Funciona con todos los proveedores de IA soportados: Las restricciones de dominio se aplican a las integraciones de OpenAI, Claude AI y xAI'
          ]
        },
        {
          id: 99,
          date: '2026-01-23',
          type: 'feature',
          title: 'Ejecución de JavaScript',
          description: 'Ejecuta código JavaScript directamente dentro de tus flujos de bot: No se requieren herramientas externas. Formatea números y texto, valida datos y entradas, itera a través de estructuras JSON y construye contenido dinámico y galerías, todo de forma nativa dentro de tu automatización.',
          changes: [
            'Reduce la dependencia de herramientas de terceros: Maneja lógica compleja sin Zapier, Make u otros intermediarios. Ahorra tiempo y costos manteniendo todo en un solo lugar'
          ]
        }
      ]
    },
    '2025': {
      '12': [
        {
          id: 96,
          date: '2025-12-03',
          type: 'feature',
          title: 'Extracción de documentos e imágenes con IA',
          description: 'Escaneo de recibos: Extrae automáticamente fecha, total y lista de artículos de recibos cargados.',
          changes: [
            'Análisis de ID y facturas: Obtiene instantáneamente detalles clave de documentos de identificación y facturas—sin necesidad de entrada manual',
            'Captura de datos escritos a mano y fotos: Convierte notas y imágenes de productos en información estructurada y utilizable'
          ]
        }
      ],
      '11': [
        {
          id: 95,
          date: '2025-11-27',
          type: 'feature',
          title: 'Respuestas de comentarios más inteligentes',
          description: 'Nuevo campo del sistema: Introducimos {{last_commented_post_text}} para proporcionar contexto completo de la publicación en la que un usuario comentó.',
          changes: [
            'Respuestas más inteligentes: Tu IA ahora puede generar respuestas a comentarios que hacen referencia directa a la publicación original en lugar de ofrecer respuestas genéricas',
            'Implementación sencilla: Agrega {{last_commented_post_text}} a tu prompt de automatización de comentarios para comenzar a usar esta función de inmediato',
            'Ejemplo de prompt: "El usuario comentó en esta publicación: {{last_commented_post_text}} — Responde de manera útil basándote en el contenido de la publicación y su comentario."'
          ]
        },
        {
          id: 94,
          date: '2025-11-25',
          type: 'improvement',
          title: 'Respuestas de Agente IA simplificadas',
          description: 'Configuración más rápida: Los Agentes IA ahora se integran directamente en tu configuración de automatización de comentarios, eliminando la necesidad de construir flujos personalizados.',
          changes: [
            'Configuración optimizada: Configura respuestas públicas y privadas todo en un solo lugar, sin pasos adicionales',
            'Experiencia mejorada: Disfruta de respuestas más inteligentes y conversacionales con un flujo de trabajo más limpio para ti y tu audiencia'
          ]
        },
        {
          id: 93,
          date: '2025-11-24',
          type: 'improvement',
          title: 'Seguimiento de mensajería masiva más inteligente + Filtros de contacto mejorados',
          description: 'Actualizaciones de mensajería masiva: Ahora verás el progreso en tiempo real (por ejemplo, 40%, 60%, 80%) mientras se envía tu transmisión, brindándote mejor visibilidad del estado de entrega.',
          changes: [
            'Gestión de contactos mejorada: Nuevos filtros en la página de Contactos—Seguimiento, Archivados y Bloqueados—facilitan organizar y segmentar tu audiencia según su estado'
          ]
        },
        {
          id: 92,
          date: '2025-11-23',
          type: 'improvement',
          title: 'Registros de administrador más rápidos + Nuevos filtros',
          description: 'Mejora de rendimiento: Los registros de actividad de administrador ahora se cargan significativamente más rápido, resolviendo retrasos anteriores y registros faltantes.',
          changes: [
            'Nuevo filtro de administrador: Visualiza fácilmente los registros por miembros individuales del equipo para obtener información más rápida y enfocada'
          ]
        },
        {
          id: 91,
          date: '2025-11-14',
          type: 'improvement',
          title: 'Filtros de Inbox permanecen guardados en todos tus dispositivos',
          description: 'Filtros persistentes: Los filtros del Inbox ahora permanecen guardados en todos los dispositivos y sesiones del navegador, eliminando la necesidad de restablecerlos cuando inicias sesión o actualizas la página.',
          changes: []
        },
        {
          id: 90,
          date: '2025-11-14',
          type: 'improvement',
          title: 'Asignaciones Round Robin más inteligentes basadas en horarios laborales',
          description: 'Enrutamiento consciente de horarios laborales: Las conversaciones se asignan solo a empleados que se encuentran actualmente dentro de sus horarios de trabajo establecidos.',
          changes: [
            'Precisión mejorada: Los miembros del equipo sin horarios laborales definidos se tratan como disponibles en todo momento',
            'Tiempos de respuesta más rápidos: Los clientes alcanzan al personal activo más rápidamente',
            'Mejor experiencia: Crea un flujo de soporte más fluido y confiable',
            'Distribución justa: Asegura que la carga de trabajo se comparta solo entre aquellos que están realmente en su turno',
            'Equipos más felices: Previene que los empleados fuera de servicio reciban nuevas asignaciones'
          ]
        },
        {
          id: 89,
          date: '2025-11-07',
          type: 'feature',
          title: 'Descripciones de CRM',
          description: 'Tooltips de etapas: Agrega descripciones a las etapas de tu pipeline para aclarar tu proceso. Cuando los miembros del equipo pasen el cursor sobre el nombre de una etapa, verán tus notas como tooltip—perfecto para alinear a tu equipo y capacitar a nuevos miembros.',
          changes: []
        },
        {
          id: 88,
          date: '2025-11-06',
          type: 'feature',
          title: 'Actualización de CRM',
          description: 'Personalización de colores de etapas: Ahora puedes personalizar tu tablero de pipeline agregando colores de fondo y texto a cada etapa. Solo haz clic en los tres puntos junto a una etapa, ve a Configuración y selecciona tus colores preferidos.',
          changes: []
        },
        {
          id: 87,
          date: '2025-11-03',
          type: 'feature',
          title: 'Obtener datos del usuario: soporte de entrada multi-formato',
          description: 'Manejo de entrada ampliado: La acción Obtener Datos del Usuario ahora soporta texto, imágenes, audio, video, archivos y ubicación en un solo paso.',
          changes: [
            'Se eliminó la restricción de tipo de entrada: Ya no requiere elegir solo un tipo de entrada por acción',
            'Ideal para flujos de IA: Perfecto para analizar envíos de medios mixtos sin necesidad de predecir el tipo de entrada'
          ]
        }
      ],
      '10': [
        {
          id: 85,
          date: '2025-10-23',
          type: 'improvement',
          title: 'Seguimiento inteligente del tiempo de respuesta',
          description: 'Medición basada en horarios laborales: Los tiempos de respuesta ahora se calculan según los horarios de trabajo de tu equipo, eliminando retrasos engañosos causados por fines de semana o días no laborables.',
          changes: [
            'Métricas de rendimiento mejoradas: visualiza datos precisos sobre cómo rinde tu equipo durante su horario real de trabajo',
            'Visibilidad sobre las respuestas fuera de horario',
            'Analíticas más justas: obtén una visión más clara de la productividad sin penalizar a tu equipo por responder fuera de su turno'
          ]
        },
        {
          id: 86,
          date: '2025-10-14',
          type: 'fix',
          title: 'Integración con canal de TikTok',
          description: 'Solucionamos un problema donde los tokens de autenticación expiraban diariamente.',
          changes: [
            'Mayor estabilidad en las cuentas de TikTok conectadas a Runamatic',
            'Los tokens de autenticación ahora tienen una duración adecuada',
            'Mejora en la fiabilidad de la conexión con TikTok'
          ]
        },
        {
          id: 87,
          date: '2025-10-09',
          type: 'feature',
          title: 'Prioridad personalizada de modelos de IA',
          description: 'Define el orden de modelos preferido: Si tu agente tiene varios proveedores de IA conectados, ahora puedes establecer qué modelo tiene prioridad al responder mensajes entrantes.',
          changes: [
            'Comportamiento anterior: el orden predeterminado era OpenAI → Claude → Gemini → xAI → DeepSeek',
            'Nuevo control: reordena tus modelos de IA según tus necesidades de flujo y rendimiento',
            'Optimiza por costo, velocidad o calidad de respuesta según tu estrategia',
            'Configura tu stack de IA exactamente como quieras dentro de Runamatic'
          ]
        }
      ],
      '9': [
        {
          id: 77,
          date: '2025-09-29',
          type: 'improvement',
          title: 'Mejoras en el flujo de chat de Instagram',
          description: 'Indicador de escritura agregado: Ahora el bot de Instagram muestra un indicador de escritura mientras genera una respuesta, creando una experiencia de mensajería más natural y fluida.',
          changes: [
            'Experiencia de mensajería más natural con indicador visual de escritura',
            'Mayor fluidez en las conversaciones automatizadas de Instagram'
          ]
        },
        {
          id: 78,
          date: '2025-09-23',
          type: 'feature',
          title: 'Automatización de mensajes en TikTok ya disponible',
          description: 'Integración con TikTok: Las empresas ahora pueden automatizar conversaciones en tiempo real en TikTok directamente desde Runamatic, desbloqueando un nuevo nivel de interacción con los clientes.',
          changes: [
            'Automatiza conversaciones en tiempo real en TikTok',
            'Nuevo canal de comunicación para llegar a tu audiencia',
            'Disponible para socios etiqueta blanca: pueden ofrecer esta integración a sus clientes'
          ]
        },
        {
          id: 79,
          date: '2025-09-16',
          type: 'feature',
          title: 'Importación de Vector Store desde OpenAI',
          description: 'Cada archivo = su propio Vector Store: Sube cualquier archivo y Runamatic creará automáticamente un Vector Store dedicado para él.',
          changes: [
            'Sube archivos y Runamatic creará automáticamente un Vector Store dedicado',
            'Expansión futura: pronto podrás agrupar varios archivos dentro de un solo Vector Store',
            'Manejo de archivos grandes: sube archivos de hasta 25 MB directamente',
            'Para archivos más grandes: crea un Vector Store en OpenAI y luego impórtalo a Runamatic',
            'Límite de dos stores por agente: cada agente de IA puede vincularse con hasta dos Vector Stores a la vez',
            'Para más stores, combina tus archivos en OpenAI antes de importarlos'
          ]
        },
        {
          id: 80,
          date: '2025-09-15',
          type: 'feature',
          title: 'Optimiza tu flujo de trabajo: comparte fácilmente pipelines y agentes de IA',
          description: 'Instalación rápida: Ahora puedes instalar pipelines y agentes de IA en otras cuentas utilizando plantillas, sin necesidad de recrear los recursos manualmente.',
          changes: [
            'Instala pipelines y agentes de IA usando plantillas',
            'Ahorra tiempo sin recrear recursos manualmente',
            'Simplifica la gestión entre múltiples cuentas'
          ]
        },
        {
          id: 81,
          date: '2025-09-08',
          type: 'improvement',
          title: 'Más control para administradores + asignaciones más inteligentes en Pipeline',
          description: 'Gestión de notificaciones personalizadas: Los administradores ahora pueden personalizar sus preferencias de notificación en cualquier cuenta de bot, sin importar su nivel de permisos.',
          changes: [
            'Antes: solo los super administradores podían personalizar notificaciones desde Configuración',
            'Ahora: cualquier admin puede ir a Mi perfil y ajustar sus alertas',
            'Asignación automática de contactos en Pipeline: cada vez que se asigna una oportunidad o ticket, el contacto asociado se asignará automáticamente al mismo administrador',
            'Flujo más rápido y eficiente sin pasos manuales'
          ]
        },
        {
          id: 82,
          date: '2025-09-04',
          type: 'feature',
          title: 'Nueva acción de Google Sheets disponible',
          description: 'Obtener múltiples filas: Nueva acción de Google Sheets que permite obtener varias filas de datos de una sola vez.',
          changes: [
            'Ideal para flujos de trabajo que usan datos estructurados en prompts del sistema',
            'Disponible para todos los usuarios',
            'Optimiza la integración con hojas de cálculo'
          ]
        },
        {
          id: 83,
          date: '2025-09-03',
          type: 'feature',
          title: 'Llamadas de voz por WhatsApp ahora compatibles',
          description: 'Usuarios de Runamatic: Las llamadas de voz ya están habilitadas.',
          changes: [
            'Las llamadas de voz están habilitadas para usuarios de Runamatic',
            'Usuarios con app personalizada: suscríbete al webhook de llamadas (v23.0) para comenzar a recibir eventos',
            'Activación desde WhatsApp Manager: ve a Configuración de la plataforma > Canales > WhatsApp > Administrar > Analíticas > Números de teléfono > [tu número] > Llamadas > Activar'
          ]
        },
        {
          id: 84,
          date: '2025-09-01',
          type: 'feature',
          title: 'Voces en tiempo real ahora disponibles en Runamatic',
          description: 'Voces en tiempo real de OpenAI: Ahora puedes usar las capacidades de voz en tiempo real de OpenAI dentro de Runamatic, lo que permite respuestas habladas instantáneas para una experiencia más dinámica y atractiva.',
          changes: [
            'Respuestas habladas instantáneas con voces de OpenAI',
            'Experiencia más dinámica y atractiva para los usuarios',
            'Interacciones de voz en tiempo real'
          ]
        }
      ],
      '8': [
        {
          id: 71,
          date: '2025-08-18',
          type: 'improvement',
          title: 'Bandeja de entrada más inteligente: ver solo lo que envían tus contactos',
          description: 'Vista solo de contactos: La bandeja de entrada ahora muestra únicamente los mensajes enviados por contactos, lo que te ayuda a identificar y responder fácilmente a las conversaciones pendientes.',
          changes: [
            'Menos confusión: Los mensajes enviados por bots, flujos o plantillas se excluyen de esta vista para simplificar tu trabajo'
          ]
        },
        {
          id: 72,
          date: '2025-08-17',
          type: 'feature',
          title: 'Lanzamiento de MM Lite para campañas de WhatsApp',
          description: 'Entrega más inteligente con MM Lite: Las campañas de marketing en WhatsApp ahora se benefician de la lógica optimizada de Meta, reduciendo mensajes no entregados y mejorando el rendimiento.',
          changes: [
            'Compatible con plantillas: Puedes seguir utilizando tus plantillas de mensajes existentes sin cambios',
            'Seguimiento avanzado: Obtén métricas más completas con soporte para Meta Pixel y CAPI',
            'Activado automáticamente: MM Lite ahora es el valor predeterminado en las campañas de marketing de WhatsApp',
            'Para gestionarlo, ve a Configuración > Canal > WhatsApp > Administrar y desplázate hacia abajo'
          ]
        },
        {
          id: 73,
          date: '2025-08-10',
          type: 'feature',
          title: 'Agentes de IA de JSON ahora en el editor de flujos',
          description: 'Construcciones complejas, simplificadas: Ahora puedes usar agentes de JSON directamente en el editor de flujos, lo que permite manejar bots complejos dentro de un solo flujo.',
          changes: [
            'Adiós a la duplicación de flujos: Ya no es necesario crear múltiples flujos para lógicas similares',
            'Los agentes de JSON hacen tus configuraciones más rápidas y eficientes',
            'Soporte para contenido extenso: Para mensajes de hasta 10.000 caracteres, utiliza campos personalizados en lugar de texto plano para mantener un buen rendimiento'
          ]
        },
        {
          id: 74,
          date: '2025-08-08',
          type: 'feature',
          title: 'GPT-5 ya disponible en Runamatic',
          description: 'Integración rápida de GPT-5: GPT-5 fue integrado en Runamatic apenas 20 minutos después de la publicación oficial de su API.',
          changes: [
            'Modelo predeterminado – GPT-5 Mini: Todos los usuarios ahora tienen acceso por defecto a GPT-5 Mini',
            'Ofrece un equilibrio entre rendimiento y velocidad',
            'Acceso personalizado a GPT-5 y GPT-5 Nano: Los modelos completos están disponibles mediante conexión con tu API key personal'
          ]
        },
        {
          id: 75,
          date: '2025-08-05',
          type: 'feature',
          title: 'Lanzamiento de File Search Assistant',
          description: 'Nueva herramienta: El File Search Assistant de OpenAI ya está disponible para todos los usuarios en el Centro de IA.',
          changes: [
            'Consejo de migración: Los asistentes existentes seguirán funcionando, pero recomendamos a todos migrar a la nueva versión',
            'Cronograma de desactivación: La versión anterior del Assistants API será retirada en diciembre de 2025'
          ]
        },
        {
          id: 76,
          date: '2025-08-01',
          type: 'feature',
          title: 'Búsqueda web nativa ahora disponible para todos',
          description: 'Consultas web en tiempo real: Pregunta al bot con búsquedas en vivo y recibe respuestas actualizadas al instante en el chat.',
          changes: [
            'Activación sencilla: Selecciona "Web Search" en el menú desplegable de AI Tools (donde normalmente eliges funciones) para encenderlo',
            'Compatibilidad de modelos: Disponible en 4.1 mini y 4.0 mini',
            'No disponible en 4.1 nano'
          ]
        }
      ],
      '7': [
        {
          id: 60,
          date: '2025-07-28',
          type: 'feature',
          title: 'Lanzamiento de "Centro de IA"',
          description: 'Nueva página pública de IA: La ruta /en/ai ya está disponible. Los partners pueden enlazarla o embeberla externamente.',
          changes: [
            'AI Hub en la barra lateral: Todos los usuarios con acceso ahora verán un "Centro de IA" en el menú lateral izquierdo',
            'Agentes: Bots personalizables que responden a los usuarios en base a prompts o flujos',
            'Funciones: Tareas que los agentes pueden activar (como verificar un pedido o stock)',
            'MCP: Permite que los agentes llamen APIs externas o ejecuten lógica en el servidor',
            'Palabras clave: Palabras o frases que activan respuestas específicas de los agentes'
          ]
        },
        {
          id: 61,
          date: '2025-07-26',
          type: 'improvement',
          title: 'Notificaciones Push más inteligentes',
          description: 'Agrupadas por defecto: Las notificaciones push en el navegador ahora se agrupan automáticamente para una experiencia más limpia y organizada.',
          changes: [
            'Revalidación automática de tokens: Los tokens se revalidan automáticamente, por lo que ya no es necesario gestionarlos manualmente'
          ]
        },
        {
          id: 62,
          date: '2025-07-15',
          type: 'feature',
          title: 'Nuevo campo personalizado de usuario: {{user_channel}}',
          description: 'Nuevo campo personalizado: Hemos agregado {{user_channel}} como un CUF, lo que permite instrucciones y reglas más naturales que se adaptan al contexto específico del canal.',
          changes: [
            'Permite instrucciones y reglas más naturales',
            'Se adapta al contexto específico del canal'
          ]
        },
        {
          id: 63,
          date: '2025-07-13',
          type: 'feature',
          title: 'Grok 4 Latest ya disponible en Runamatic',
          description: 'Nueva opción de modelo de IA: Ahora puedes seleccionar Grok 4 Latest para tus agentes de IA dentro de Runamatic.',
          changes: [
            'Grok 4 ofrece mayor velocidad',
            'Inteligencia más avanzada y un rendimiento de IA más preciso',
            'Perfecto para equipos que buscan llevar al límite el poder de los chatbots con IA'
          ]
        },
        {
          id: 64,
          date: '2025-07-02',
          type: 'feature',
          title: 'Tarjetas y galerías automáticas',
          description: 'Tarjetas y galerías automáticas: Nuestro sistema ahora genera automáticamente tarjetas y galerías visualmente atractivas. Solo conecta MCP a tu prompt y observa cómo funciona.',
          changes: [
            'Instrucción para prompt con MCP: Agrega especificaciones JSON para formato automático',
            'Compatible con textos, botones, imágenes, galerías y videos',
            'Formato según las pautas de Messenger Messaging Platform'
          ]
        },
        {
          id: 65,
          date: '2025-07-01',
          type: 'feature',
          title: 'MCP Integration ya está activo',
          description: 'Integración con sistemas externos: Ahora puedes conectar tus bots a sistemas externos usando MCP (Multi-Channel Processing) Servers directamente en Runamatic.',
          changes: [
            'Los agentes pueden obtener datos de productos en tiempo real',
            'Actualizar carritos de Shopify',
            'Consultar políticas o inventario de la tienda',
            'Conectarse a endpoints personalizados de MCP',
            'Integración a nivel de prompt: Agrega funcionalidad MCP directamente dentro del editor de prompts'
          ]
        }
      ],
      '6': [
        {
          id: 45,
          date: '2025-06-24',
          type: 'feature',
          title: 'Nuevo modelo de IA disponible',
          description: 'Vista previa de Gemini 2.5 Flash Lite: ya puedes seleccionar un modelo más rápido y liviano.',
          changes: [
            'Requiere clave API propia',
            'Ideal para tareas con alto volumen y tiempos de respuesta mínimos'
          ]
        },
        {
          id: 46,
          date: '2025-06-23',
          type: 'feature',
          title: 'Campos personalizados de selección única y múltiple',
          description: 'Nueva función: ahora puedes crear campos personalizados para contactos con listas desplegables de selección única o múltiple.',
          changes: [
            'Selección única: permite una sola opción, ideal para campos como "País" o "Tipo de suscripción"',
            'Selección múltiple: permite elegir varias opciones, útil para "Temas de interés" o "Canales preferidos"',
            'Beneficio: estandariza la carga de datos y mejora la calidad de tus registros de contacto'
          ]
        },
        {
          id: 47,
          date: '2025-06-20',
          type: 'feature',
          title: 'Campos multiselección ahora compatibles en pipelines',
          description: 'Campos personalizados multiselección: ahora puedes usarlos dentro de tus pipelines.',
          changes: [
            'Asigna múltiples opciones por entrada, organiza mejor tus datos y crea etapas más dinámicas para ventas o soporte'
          ]
        },
        {
          id: 48,
          date: '2025-06-13',
          type: 'feature',
          title: 'Filtros por campos personalizados en el pipeline',
          description: 'Filtros avanzados: puedes filtrar oportunidades y tickets usando cualquier campo personalizado que hayas creado.',
          changes: [
            'Encuentra fácilmente los registros que necesitas y ahorra tiempo gestionando tus flujos de ventas o soporte'
          ]
        },
        {
          id: 49,
          date: '2025-06-11',
          type: 'feature',
          title: 'Etiquetas de estado personalizadas para el pipeline',
          description: 'Estados personalizados: reemplaza las opciones por defecto (Abierto/Ganado/Perdido) con tus propios estados.',
          changes: [
            'Adapta los flujos del pipeline a los procesos reales de tu empresa para mayor claridad y adopción del equipo'
          ]
        },
        {
          id: 50,
          date: '2025-06-10',
          type: 'feature',
          title: 'Nueva condición "Sin valor" en campos personalizados',
          description: 'Filtrado mejorado: puedes detectar si un campo personalizado no tiene valor asignado.',
          changes: [
            'Segmenta contactos incompletos, automatiza flujos con mayor precisión y mejora tu calidad de datos'
          ]
        },
        {
          id: 51,
          date: '2025-06-08',
          type: 'feature',
          title: 'Condiciones de oportunidades en pipeline',
          description: 'Condiciones según estado de oportunidad: ahora puedes verificar si un contacto tiene una oportunidad abierta, ganada o perdida.',
          changes: [
            'Activa automatizaciones sin necesidad de soluciones complejas'
          ]
        },
        {
          id: 52,
          date: '2025-06-06',
          type: 'feature',
          title: 'Campos obligatorios y opcionales en el pipeline',
          description: 'Control de requerimientos: define qué campos son obligatorios u opcionales en Oportunidades y Tickets.',
          changes: [
            'Asegura la captura de información crítica y mantiene flexibilidad en los detalles adicionales'
          ]
        },
        {
          id: 53,
          date: '2025-06-05',
          type: 'feature',
          title: 'Campos de datos personalizados y flujos más potentes',
          description: 'Mayor capacidad en flujos: ahora puedes ejecutar hasta 100 acciones seguidas sin requerir interacción del usuario.',
          changes: [
            'Campos personalizados en pipeline: crea campos específicos para Oportunidades y Tickets según tus procesos internos',
            'Flujos más sofisticados, menos interrupciones'
          ]
        },
        {
          id: 54,
          date: '2025-06-02',
          type: 'improvement',
          title: 'Visibilidad completa de fechas de vencimiento',
          description: 'Todas las fechas a la vista: ahora cada oportunidad y ticket muestra su fecha de vencimiento, no solo los vencidos.',
          changes: [
            'Mejora la planificación y evita retrasos'
          ]
        }
      ],
      '5': [
        {
          id: 35,
          date: '2025-05-28',
          type: 'feature',
          title: 'Filtros por fecha de vencimiento',
          description: 'Nuevos filtros por fecha de vencimiento: Ordena rápidamente oportunidades y tickets según "Hoy", "Esta semana" o rango personalizado.',
          changes: [
            'Identifica lo vencido o lo próximo para mantener las tareas al día'
          ]
        },
        {
          id: 36,
          date: '2025-05-28',
          type: 'feature',
          title: 'Fechas de vencimiento en pipelines',
          description: 'Fechas de vencimiento en oportunidades y etapas: Ahora puedes asignar fechas límite a etapas o a oportunidades dentro de un pipeline.',
          changes: [
            'Ayuda a mantener organizado al equipo y asegura el cumplimiento de los plazos'
          ]
        },
        {
          id: 37,
          date: '2025-05-27',
          type: 'feature',
          title: 'Duplicación de navegación en cuentas white-label',
          description: 'Duplicación del menú de navegación de cuentas: Ahora puedes copiar fácilmente el menú de navegación dentro de tu configuración de white-label.',
          changes: [
            'Permite replicar rápidamente estructuras de menú en varias cuentas y mantener una navegación coherente entre marcas'
          ]
        },
        {
          id: 38,
          date: '2025-05-26',
          type: 'feature',
          title: 'Transcripción automática de audios',
          description: 'Transcripción de mensajes de audio en el Inbox: Los mensajes de voz ahora se transcriben automáticamente y aparecen como texto.',
          changes: [
            'Revisión más rápida',
            'Mejor registro de conversaciones',
            'Mayor productividad sin necesidad de escuchar cada audio'
          ]
        },
        {
          id: 39,
          date: '2025-05-24',
          type: 'feature',
          title: 'Filtrado por cambios recientes',
          description: 'Nuevas opciones de filtrado: Filtra oportunidades y tickets según el último cambio de etapa o el último comentario agregado.',
          changes: [
            'Facilita el seguimiento del progreso y mejora la gestión del pipeline'
          ]
        },
        {
          id: 40,
          date: '2025-05-21',
          type: 'feature',
          title: 'Transferencia de oportunidades entre pipelines',
          description: 'Transferencias fluidas de oportunidades: Ahora puedes mover oportunidades o tickets directamente de un pipeline a otro sin tener que recrearlas manualmente.',
          changes: [
            'Ahorra tiempo, mejora la organización del equipo y mantiene los flujos de trabajo activos y sincronizados'
          ]
        },
        {
          id: 41,
          date: '2025-05-19',
          type: 'improvement',
          title: 'Notificaciones multilingües',
          description: 'Soporte multilingüe expandido para notificaciones: Las notificaciones de la plataforma ahora se envían en inglés, portugués, español, francés, alemán y vietnamita.',
          changes: [
            'Cada usuario recibe actualizaciones importantes en su idioma preferido para una experiencia más inclusiva'
          ]
        },
        {
          id: 42,
          date: '2025-05-16',
          type: 'improvement',
          title: 'Identificación de acciones en el Inbox',
          description: 'Registro detallado de acciones en el Inbox: Los registros de actividad ahora muestran qué acción se realizó y qué usuario la ejecutó.',
          changes: [
            'Refuerza la responsabilidad del equipo',
            'Mejora la transparencia y supervisión',
            'Permite identificar rápidamente qué usuario hizo un cambio para una solución más ágil',
            'Brinda total claridad y control sobre toda la actividad del inbox a nivel de usuario'
          ]
        },
        {
          id: 43,
          date: '2025-05-15',
          type: 'feature',
          title: 'Personalización de notificaciones para administradores',
          description: 'Notificaciones de administrador personalizables: Ahora puedes modificar los mensajes de notificación para administradores directamente desde la interfaz.',
          changes: [
            'Ve a WLApp → SelfOnboarding para realizar los cambios',
            '(Video tutorial próximamente)'
          ]
        },
        {
          id: 44,
          date: '2025-05-04',
          type: 'improvement',
          title: 'Mejora en animación de escritura',
          description: 'Animación de escritura en WhatsApp: Ahora se muestra automáticamente la animación de "escribiendo" cada vez que se envía un mensaje, brindando una experiencia de chat más fluida.',
          changes: [
            'Indicador de escritura para acciones de IA',
            'Runamatic envía automáticamente la animación de escritura cuando se ejecuta una acción de inteligencia artificial, informando al usuario que el bot está procesando su respuesta'
          ]
        }
      ],
      '4': [
        {
          id: 24,
          date: '2025-04-30',
          type: 'feature',
          title: 'Notificaciones por Email Corporativo y Automatizaciones para Administradores',
          description: 'Alertas por correo personalizadas: los mensajes del sistema ahora se envían desde el correo SMTP propio del negocio (cuando el canal de Email está conectado).',
          changes: [
            'Disparador de Asignación de Conversación: se pueden lanzar automatizaciones cuando cambia la propiedad de un chat',
            'Nuevo campo del sistema: {{assigned_admin_email}} disponible para flujos, plantillas e informes'
          ]
        },
        {
          id: 25,
          date: '2025-04-28',
          type: 'improvement',
          title: 'Mejoras en WhatsApp',
          description: 'Auto suscripción y cancelación: los contactos que pausan o reanudan mensajes desde WhatsApp se actualizan en tiempo real.',
          changes: [
            'Plantillas mejoradas: los botones ahora inician flujos de manera confiable, y cada plantilla incluye botón de "Copiar código"',
            'Corrección en tarjetas: la bandeja de entrada ahora muestra imagen y texto nuevamente en las tarjetas de WhatsApp'
          ]
        },
        {
          id: 26,
          date: '2025-04-27',
          type: 'feature',
          title: 'Nuevos Modelos de IA y Control de Imágenes',
          description: 'Nuevas opciones LLM: se agregaron Gemini 2.5 Flash, Gemini 2.5 Pro y Grok 3 al bloque "Generar Texto".',
          changes: [
            'Control de calidad de imagen (Gemini): usa gpt-image-1 para elegir entre salida Alta, Estándar o Liviana'
          ]
        },
        {
          id: 27,
          date: '2025-04-25',
          type: 'improvement',
          title: 'Ajustes en Oportunidades, Calendario y Contactos',
          description: 'Edición rápida de etapas: cambia la etapa de la oportunidad directamente desde la tarjeta o al comentar.',
          changes: [
            'Selección de calendario: elige cualquier ID de calendario conectado desde Google para sincronizar reuniones',
            'Renombrado más fluido: renombrar un contacto o usuario concatena automáticamente nombre y apellido',
            'Crear contactos sin email: ahora se pueden agregar leads desde la bandeja con solo número telefónico'
          ]
        },
        {
          id: 28,
          date: '2025-04-24',
          type: 'feature',
          title: 'Actualización del Generador de Imágenes con IA',
          description: 'Nuevo modelo de OpenAI integrado: el modelo más reciente (lanzado el 23 de abril) ya está disponible en el bloque "Generar Imagen".',
          changes: [
            'Control de calidad incluido: elige entre Estándar, Detalle Alto o Liviano para equilibrar velocidad y fidelidad'
          ]
        },
        {
          id: 29,
          date: '2025-04-22',
          type: 'improvement',
          title: 'Estabilidad y Protección en Difusiones',
          description: 'Difusiones sin goteo: los envíos masivos ya no pueden suscribir contactos a campañas de goteo, protegiendo los recursos del sistema.',
          changes: [
            'Límite de pasos WAIT: una difusión solo puede contener un paso WAIT consecutivo',
            'Tiempos locales: el Registro Activo de Admin ahora muestra horarios en zona local en lugar de UTC',
            'Correcciones en Messenger e Instagram: nombres de archivos con caracteres especiales se envían correctamente y las difusiones llegan a contactos de Instagram'
          ]
        },
        {
          id: 30,
          date: '2025-04-21',
          type: 'improvement',
          title: 'Mejoras en Flow Builder y Permisos',
          description: 'Pruebas con IP fija: ahora las cuentas con IP estática pueden probar solicitudes directamente desde Flow Builder.',
          changes: [
            'Estabilidad en campos personalizados: sumar o restar valores numéricos, fechas u horas ya no daña los datos',
            'Fiabilidad del asistente OpenAI: el bot ahora responde incluso si el contacto envía varios mensajes en rápida sucesión',
            'Parche de permisos en Pipeline: administradores con acceso restringido a "ver asignados" pueden ver oportunidades que ellos mismos asignaron'
          ]
        },
        {
          id: 31,
          date: '2025-04-20',
          type: 'feature',
          title: 'Lanzamiento de Analíticas del Pipeline',
          description: 'Panel de analítica del pipeline: métricas completas sobre flujo de ventas, tasa de éxito y velocidad comercial.',
          changes: [
            'Análisis de caídas y proyecciones: detecta cuellos de botella y ajusta pronósticos en tiempo real'
          ]
        },
        {
          id: 32,
          date: '2025-04-19',
          type: 'improvement',
          title: 'Mejoras en Voz y Omnicanalidad',
          description: 'Forzar contacto por voz (API): nuevo parámetro permite crear contactos exclusivamente de voz, sin WhatsApp o SMS.',
          changes: [
            'Permisos en cámara/micrófono desde IFRAME: los dashboards embebidos ahora pueden solicitar acceso a micro y cámara',
            'Compartir ubicación en todos los canales: los usuarios pueden enviar su ubicación en vivo desde cualquier canal compatible',
            'Campo de error en última entrada: {{last_input_failure}} devuelve "timeout", "user_skipped" o "invalid_input_attempts"'
          ]
        },
        {
          id: 33,
          date: '2025-04-17',
          type: 'feature',
          title: 'Nuevos Modelos GPT-4.1',
          description: 'Modelos OpenAI agregados: GPT-4.1, GPT-4.1 mini y GPT-4.1 nano ya están disponibles en los bloques de texto y asistentes.',
          changes: [
            'GPT-4.1: máxima precisión en razonamiento',
            'GPT-4.1 mini: 2-3× más rápido, menor costo por token, ideal para chat',
            'GPT-4.1 nano: ultraliviano, ideal para flujos de alto volumen o respaldo',
            'Integra tu propia clave de OpenAI: agrégala en Configuración ▸ Integraciones ▸ OpenAI'
          ]
        },
        {
          id: 34,
          date: '2025-04-13',
          type: 'feature',
          title: 'Bandeja de Entrada dentro del Pipeline',
          description: 'Vista todo en uno: envía mensajes, lanza flujos y accede al historial completo sin salir del panel de Pipeline.',
          changes: [
            'Sin cambio de pestañas: actualiza tratos y chatea con leads desde una misma pantalla — entregas más rápidas y foco total'
          ]
        }
      ],
      '3': [
        {
          id: 16,
          date: '2025-03-25',
          type: 'feature',
          title: 'Actualización de voz y modelos',
          description: 'Control de tono de voz y emoción en agentes IA: Runamatic ahora permite personalizar el tono y la emoción de voz de tus agentes directamente desde los prompts.',
          changes: [
            'Configura tonos como alegre, relajado o motivacional',
            'Crea experiencias únicas con agentes que tienen personalidad propia',
            'Integración perfecta en todos los canales para una experiencia personalizada',
            'Gemini 2.5 Pro Experimental ya disponible',
            'Razonamiento unificado con soporte de contexto extendido',
            'Desempeño más inteligente y rápido en las conversaciones',
            'Disponible para todas las cuentas de Runamatic y sus partners'
          ]
        },
        {
          id: 17,
          date: '2025-03-22',
          type: 'improvement',
          title: 'Mejora en herramienta de base de conocimiento',
          description: 'Extracción avanzada de enlaces con nuevo protocolo: Ahora puedes extraer hasta 500 enlaces desde un solo dominio.',
          changes: [
            'Mayor eficiencia en el procesamiento de contenido',
            'Optimizado para construir grandes bases de conocimiento de forma más rápida y precisa'
          ]
        },
        {
          id: 18,
          date: '2025-03-20',
          type: 'improvement',
          title: 'Nuevas funciones y mejoras',
          description: 'Actualización de todos los modelos de Google: Mejora en velocidad y confiabilidad en respuestas.',
          changes: [
            'Edición de imágenes con Gemini',
            'Ya disponible para mejoras visuales impulsadas por IA'
          ]
        },
        {
          id: 19,
          date: '2025-03-17',
          type: 'feature',
          title: 'Actualización rápida del Inbox',
          description: 'Nuevo almacenamiento de archivos para agentes: Puedes guardar directamente en el inbox los documentos o archivos que suelen compartir los agentes humanos.',
          changes: [
            'Facilita el acceso y el reenvío rápido en conversaciones'
          ]
        },
        {
          id: 20,
          date: '2025-03-12',
          type: 'feature',
          title: 'Nuevas funciones y mejoras',
          description: 'Visibilidad de funciones en acciones ejecutadas: Ahora puedes ver las funciones utilizadas dentro de cada acción ejecutada en el Inbox.',
          changes: [
            'Mejora el análisis, la depuración y la transparencia'
          ]
        },
        {
          id: 21,
          date: '2025-03-10',
          type: 'improvement',
          title: 'Nuevas funciones y correcciones',
          description: 'Integración con MailChimp: Guarda automáticamente contactos de clientes desde tu chatbot en tu cuenta de MailChimp.',
          changes: [
            'Corrección de errores en calendarios externos',
            'Solucionado el problema que causaba entradas duplicadas',
            'Flujos predeterminados actualizados',
            'Las nuevas cuentas ahora incluyen flujos mejorados con videos tutoriales'
          ]
        },
        {
          id: 22,
          date: '2025-03-08',
          type: 'improvement',
          title: 'Actualización de interfaz',
          description: 'Mejoras en el modo oscuro: Interfaz refinada para mayor visibilidad y contraste.',
          changes: [
            'Actualizaciones en el Flow Builder',
            'Canvas actualizado',
            'Nodos más intuitivos para una edición más clara y rápida'
          ]
        },
        {
          id: 23,
          date: '2025-03-05',
          type: 'feature',
          title: 'Nuevas funciones y mejoras',
          description: 'Rediseño del Constructor de Base de Conocimiento: Interfaz renovada para una navegación más fluida, mejor extracción de contenido y edición de prompts.',
          changes: [
            'Ingresa una URL para extraer contenido de múltiples páginas',
            'Convierte páginas web o productos de Shopify a formato JSON estructurado',
            'Genera ejemplos de preguntas y respuestas a partir del contenido',
            'Mejora el contenido con asistencia de IA',
            'Arrastra o haz clic para subir archivos (soporta PDF, DOCX y TXT)',
            'Enlace de YouTube: Extrae el contenido de videos desde un enlace de YouTube',
            'Enlace de sitio web: Extrae páginas ingresando la URL del sitio',
            'Acciones adicionales: Obtener páginas, extraer contenido, prompt combinado final, convertir a JSON, copiar, guardar y limpiar'
          ]
        }
      ],
      '2': [
        {
          id: 10,
          date: '2025-02-23',
          type: 'improvement',
          title: 'Controles mejorados para administradores en el pipeline',
          description: 'Visibilidad refinada para administradores: ahora los administradores solo pueden ver las oportunidades asignadas a ellos.',
          changes: [
            'Protección de datos mejorada',
            'Organización optimizada para equipos en expansión',
            'Mayor eficiencia en la gestión de oportunidades'
          ]
        },
        {
          id: 11,
          date: '2025-02-15',
          type: 'improvement',
          title: 'Nuevas funciones y mejoras',
          description: 'Disparadores actualizados para reglas: se mejoraron las reglas de automatización para un comportamiento más preciso del asistente de ventas.',
          changes: [
            'Nuevas condiciones para los disparadores',
            'Mejor manejo de lógica para flujos de trabajo más fluidos'
          ]
        },
        {
          id: 12,
          date: '2025-02-13',
          type: 'fix',
          title: 'Comportamiento restaurado en el Webchat',
          description: 'El botón Enter vuelve a enviar los mensajes: se restauró el comportamiento original del chat web.',
          changes: [
            'Presionar Enter envía el mensaje',
            'Mejora la experiencia de conversación familiar para los usuarios'
          ]
        },
        {
          id: 14,
          date: '2025-02-06',
          type: 'improvement',
          title: 'Mejora en el ingreso de texto del Webchat',
          description: 'Entrada de mensajes mejorada: ahora presionar Enter crea una nueva línea.',
          changes: [
            'Para enviar el mensaje, mantener Tab + Enter',
            'Más control sobre la escritura en conversaciones largas'
          ]
        },
        {
          id: 15,
          date: '2025-02-01',
          type: 'feature',
          title: 'Nuevo modelo de IA disponible',
          description: 'Compatible con OpenAI 3o Mini: respuestas más rápidas, mayor precisión y eficiencia.',
          changes: [
            'Puedes usar tu propia clave de OpenAI para comenzar'
          ]
        }
      ],
      '1': [
        {
          id: 1,
          date: '2025-01-31',
          type: 'feature',
          title: 'Actualización de Inbox y Pipeline',
          description: 'Integración completa del Pipeline en el Inbox para una gestión mejorada de leads y oportunidades.',
          changes: [
            'Leads y oportunidades ahora se visualizan directamente desde las conversaciones',
            'Gestiona etapas del lead sin salir del inbox',
            'Mueve oportunidades entre etapas con un clic',
            'Accede a todos los detalles del lead desde la vista del pipeline',
            'Edita detalles de oportunidades, ajusta estado y prioridad',
            'Colabora en tiempo real con tu equipo'
          ]
        },
        {
          id: 2,
          date: '2025-01-26',
          type: 'improvement',
          title: 'Actualizaciones de plataforma',
          description: 'Integración con DeepSeek habilitada y mejoras en la compatibilidad de modelos LLM.',
          changes: [
            'DeepSeek está activo en todas las cuentas de Runamatic',
            'No es compatible con funciones: evitar establecerlo como modelo LLM predeterminado',
            'Usar solo en escenarios avanzados que requieran razonamiento complejo',
            'Solicitudes específicas por cliente disponibles contactando al soporte'
          ]
        },
        {
          id: 3,
          date: '2025-01-22',
          type: 'improvement',
          title: 'Actualización de infraestructura',
          description: 'Migración a arquitectura multi-servidor con mejoras significativas en rendimiento y escalabilidad.',
          changes: [
            'Migración a servidores dedicados para la app y la base de datos',
            'Infraestructura empresarial de Google con mayor escalabilidad',
            'Balanceo de carga automático y sistemas de recuperación ante fallos',
            'Arquitectura dinámica que escala automáticamente ante picos de tráfico'
          ]
        },
        {
          id: 4,
          date: '2025-01-17',
          type: 'feature',
          title: 'Nuevas funciones y mejoras',
          description: 'Detección automática de URL en Webchat y automatizaciones avanzadas para pipelines.',
          changes: [
            'Se agregó el campo {{webchat_parent_url}} para detectar automáticamente la página que el visitante está navegando',
            'Mejora la personalización al capturar el contexto de navegación',
            'Aumenta la tasa de conversión mediante interacciones más precisas',
            'Nuevos disparadores y reglas permiten flujos de trabajo más personalizados',
            'Límite ampliado de caracteres: hasta 50.000 en código y 20.000 en CSS personalizado',
            'Soporte de traducciones vía mainLangs.en.translations.hello = \'Howdy\';'
          ]
        },
        {
          id: 5,
          date: '2025-01-11',
          type: 'improvement',
          title: 'Actualizaciones de Webchat y Magic Links',
          description: 'Mejoras en la visualización del nombre en Webchat y corrección de problemas en Magic Links.',
          changes: [
            'Ahora muestra únicamente el primer nombre del visitante, en lugar de "Usuario invitado"',
            'Solucionados los problemas de espaciado en los títulos generados automáticamente'
          ]
        },
        {
          id: 6,
          date: '2025-01-08',
          type: 'feature',
          title: 'Actualización Beta del CRM',
          description: 'Nuevo CRM beta disponible con herramientas mejoradas para gestión de contactos, leads y oportunidades.',
          changes: [
            'Accesible desde el menú de navegación de Runamatic',
            'Incluye herramientas mejoradas y experiencia más fluida',
            'Gestión optimizada de contactos, leads y oportunidades'
          ]
        },
        {
          id: 8,
          date: '2025-01-03',
          type: 'improvement',
          title: 'Nuevas funciones y mejoras',
          description: 'Mejoras en Flow Builder, gestión de campos personalizados y funciones del Webchat.',
          changes: [
            'Campo de texto ampliado para facilitar la edición en dispositivos iOS',
            'Interfaz optimizada con nueva ventana de configuración y vista en tabla',
            'Entrada de texto expandida para una experiencia de usuario más dinámica'
          ]
        },
        {
          id: 9,
          date: '2025-01-01',
          type: 'feature',
          title: 'Funciones revolucionarias en Runamatic',
          description: 'Campos personalizados de usuario en Agentes y transcripciones de video para la Base de Conocimiento.',
          changes: [
            'Simplifican flujos de trabajo y permiten personalizar interacciones directamente desde los agentes',
            'Personalización a escala',
            'Creación más rápida con configuraciones simplificadas',
            'Flujos de trabajo optimizados con menos configuraciones manuales',
            'Extrae y añade transcripciones de videos de YouTube para ampliar el conocimiento del agente inteligente'
          ]
        }
      ]
    }
  }), []);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'feature':
        return 'fas fa-plus-circle';
      case 'improvement':
        return 'fas fa-arrow-up';
      case 'fix':
        return 'fas fa-wrench';
      default:
        return 'fas fa-info-circle';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'feature':
        return '#10B981';
      case 'improvement':
        return '#3B82F6';
      case 'fix':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'feature':
        return 'Nueva funcionalidad';
      case 'improvement':
        return 'Mejora';
      case 'fix':
        return 'Corrección';
      default:
        return 'Actualización';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getMonthName = (monthNumber) => {
    const months = {
      '1': 'Enero', '2': 'Febrero', '3': 'Marzo', '4': 'Abril',
      '5': 'Mayo', '6': 'Junio', '7': 'Julio', '8': 'Agosto',
      '9': 'Septiembre', '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre'
    };
    return months[monthNumber] || monthNumber;
  };

  useEffect(() => {
    const data = changelogData[selectedYear];
    if (data) {
      // Convertir datos a formato para acordeón
      const monthsData = Object.entries(data)
        .map(([month, changes]) => ({
          month,
          changes: changes.sort((a, b) => new Date(b.date) - new Date(a.date))
        }))
        .sort((a, b) => b.month - a.month);
      setYearData(monthsData);
    } else {
      setYearData([]);
    }
  }, [selectedYear, changelogData]);

  const availableYears = Object.keys(changelogData).sort((a, b) => b - a);

  const toggleMonth = (month) => {
    const newExpanded = new Set(expandedMonths);
    if (newExpanded.has(month)) {
      newExpanded.delete(month);
    } else {
      newExpanded.add(month);
    }
    setExpandedMonths(newExpanded);
  };

  const getTotalChanges = () => {
    return yearData.reduce((total, monthData) => total + monthData.changes.length, 0);
  };

  return (
    <div className="changelog-page">
      <div className="changelog-background">
        <div className="changelog-shapes">
          <div className="changelog-shape changelog-shape-1"></div>
          <div className="changelog-shape changelog-shape-2"></div>
          <div className="changelog-shape changelog-shape-3"></div>
        </div>
      </div>

      <div className="changelog-container">
        {/* Header */}
        <motion.div 
          className="changelog-header"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="changelog-title">
            <i className="fas fa-code-branch"></i>
            Registro de cambios
          </h1>
          <p className="changelog-subtitle">
            Mantente al día con las últimas actualizaciones y mejoras de Runamatic
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div 
          className="changelog-filters"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="filter-group">
            <label htmlFor="year-select">Año:</label>
            <select 
              id="year-select"
              value={selectedYear} 
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setExpandedMonths(new Set());
              }}
              className="filter-select"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="filter-info">
            <span className="changes-count">
              {getTotalChanges()} {getTotalChanges() === 1 ? 'cambio' : 'cambios'} en {selectedYear}
            </span>
          </div>
        </motion.div>

        {/* Changelog Items */}
        <div className="changelog-content">
          <AnimatePresence mode="wait">
            {yearData.length > 0 ? (
              <motion.div
                key={selectedYear}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="changelog-accordion"
              >
                {yearData.map((monthData, index) => (
                  <motion.div
                    key={monthData.month}
                    className="changelog-month-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <div 
                      className={`changelog-month-header ${expandedMonths.has(monthData.month) ? 'expanded' : ''}`}
                      onClick={() => toggleMonth(monthData.month)}
                    >
                      <div className="month-header-left">
                        <h3 className="month-title">{getMonthName(monthData.month)} {selectedYear}</h3>
                        <span className="month-count">{monthData.changes.length} {monthData.changes.length === 1 ? 'actualización' : 'actualizaciones'}</span>
                      </div>
                      <div className="month-header-right">
                        <i className={`fas fa-chevron-down ${expandedMonths.has(monthData.month) ? 'rotated' : ''}`}></i>
                      </div>
                    </div>

                    <AnimatePresence>
                      {expandedMonths.has(monthData.month) && (
                        <motion.div
                          className="changelog-month-content"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="changelog-items">
                            {monthData.changes.map((change, changeIndex) => (
                              <motion.div
                                key={change.id}
                                className="changelog-item"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.3, delay: changeIndex * 0.05 }}
                              >
                                <div className="changelog-item-header">
                                  <div className="changelog-item-left">
                                    <div 
                                      className="changelog-type-badge"
                                      style={{ backgroundColor: getTypeColor(change.type) }}
                                    >
                                      <i className={getTypeIcon(change.type)}></i>
                                      <span>{getTypeLabel(change.type)}</span>
                                    </div>
                                    <div className="changelog-version">
                                      <span className="version-date">{formatDate(change.date)}</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="changelog-item-content">
                                  <h4 className="changelog-item-title">{change.title}</h4>
                                  <p className="changelog-item-description">{change.description}</p>
                                  
                                  <div className="changelog-item-changes">
                                    <h5>Cambios incluidos:</h5>
                                    <ul>
                                      {change.changes.map((changeItem, itemIndex) => (
                                        <motion.li
                                          key={itemIndex}
                                          initial={{ opacity: 0, x: -10 }}
                                          animate={{ opacity: 1, x: 0 }}
                                          transition={{ duration: 0.2, delay: (changeIndex * 0.05) + (itemIndex * 0.02) }}
                                        >
                                          <i className="fas fa-check"></i>
                                          {changeItem}
                                        </motion.li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="changelog-empty"
              >
                <div className="empty-illustration">
                  <i className="fas fa-search"></i>
                </div>
                <h3>No hay cambios para mostrar</h3>
                <p>No se encontraron actualizaciones para el año seleccionado.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>


      </div>
    </div>
  );
};

export default ChangelogPage; 